import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Home, Users, Calendar as CalendarIcon, RefreshCw, Maximize2, Minimize2, ChevronLeft, ChevronRight, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getStatusStyle, shiftDurationHours } from './calendarUtils';
import { HouseDayView } from './HouseDayView';   // UC-D-2 (UI-21, D6)

// UC-D-2: the By SIL House drill-down is the house day view (same 640 px + expand sheet family as UC-A/UC-B; no route),
// backed by the read-only GET /shared-delivery/house-day. The pre-UC-D house details / week shift list remain below it.
export function CalendarSILHouseDrawer({ houseId, allShifts, open, onClose, onShiftClick, staff = [], onChanged, onOpenGroup, onPublish, initialDate }) {
  const [house, setHouse] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [day, setDay] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [dayError, setDayError] = useState('');
  const loadDay = async (d) => {
    if (!houseId || !d) return;
    try { const r = await api.get('/shared-delivery/house-day', { params: { house_id: houseId, date: d } }); setDayData(r.data?.data || null); setDayError(''); }
    catch (e) { setDayData(null); setDayError(e?.response?.data?.detail?.message || 'Could not load the house day'); }
  };
  useEffect(() => {
    if (!open) return;
    const dates = allShifts.filter((s) => s.house_id === houseId || s._sil_house_id === houseId).map((s) => s.date).sort();
    const today = new Date().toISOString().slice(0, 10);
    setDay(initialDate || (dates.includes(today) ? today : dates[0]) || today);
  }, [open, houseId, allShifts, initialDate]);
  useEffect(() => { if (open && day) loadDay(day); }, [open, day, houseId]); // eslint-disable-line react-hooks/exhaustive-deps
  const stepDay = (n) => { const d = new Date(day); d.setDate(d.getDate() + n); setDay(d.toISOString().slice(0, 10)); };
  const refreshAll = () => { loadDay(day); onChanged && onChanged(); };
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !houseId) return;
    setLoading(true);
    Promise.all([
      api.get(`/houses/${houseId}`).catch(() => ({ data: { data: null } })),
      api.get(`/accommodation/by-house/${houseId}`).catch(() => ({ data: [] })),
    ]).then(([hRes, aRes]) => {
      setHouse(hRes.data?.data || hRes.data || null);
      const allocs = Array.isArray(aRes.data) ? aRes.data : (aRes.data?.items || aRes.data?.data || []);
      setAllocations(allocs.filter(a => a.status === 'ACTIVE'));
    }).finally(() => setLoading(false));
  }, [open, houseId]);

  const houseShifts = useMemo(() => {
    return allShifts.filter(s => (s.house_id === houseId || s._sil_house_id === houseId)).sort((a, b) => `${a.date} ${a.start_time}`.localeCompare(`${b.date} ${b.start_time}`));
  }, [allShifts, houseId]);

  const totalHours = houseShifts.reduce((acc, s) => acc + shiftDurationHours(s), 0);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className={`${expanded ? 'w-full sm:max-w-none' : 'w-full sm:w-[640px] sm:max-w-[640px]'} overflow-y-auto`} data-testid="sil-house-drawer" data-expanded={expanded ? 'true' : 'false'}>
        <SheetHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <SheetTitle className="flex items-center gap-2"><Home className="w-4 h-4 text-emerald-600" /> {dayData?.house?.name || house?.name || 'SIL house'} · day view</SheetTitle>
              <SheetDescription className="flex items-center gap-1.5 flex-wrap tabular-nums" data-testid="house-day-header">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => stepDay(-1)} data-testid="house-day-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                <span data-testid="house-day-range">{dayData ? `${dayData.window.date} ${dayData.window.start_label} → ${dayData.window.end_date} ${dayData.window.end_label.slice(0, 5)}` : day}</span>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => stepDay(1)} data-testid="house-day-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
                {dayData && <span data-testid="house-day-counts">· {dayData.counts.residents} resident{dayData.counts.residents === 1 ? '' : 's'} · {dayData.counts.workers} worker{dayData.counts.workers === 1 ? '' : 's'}</span>}
                {dayData?.house?.location && <span>· {dayData.house.location}</span>}
                {dayData?.group_id && <button type="button" onClick={() => onOpenGroup && onOpenGroup(dayData.group_id)} className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] text-sky-800" data-testid="house-header-group-chip"><Link2 className="w-3 h-3" /> group</button>}
              </SheetDescription>
            </div>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hidden sm:inline-flex" onClick={() => setExpanded((v) => !v)} title={expanded ? 'Dock to the side' : 'Expand to full page'} data-testid="house-expand-toggle">{expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}</Button>
          </div>
        </SheetHeader>
        <div className="mt-3">
          {dayError && <div className="text-xs text-red-700" data-testid="house-day-error">{dayError}</div>}
          {!dayData && !dayError && <div className="text-xs text-muted-foreground" data-testid="house-day-loading">Loading house day…</div>}
          {dayData && <HouseDayView data={dayData} staff={staff} onChanged={refreshAll} onOpenShift={(sid) => { const known = allShifts.find((s) => s.id === sid); if (known) onShiftClick(known); else api.get(`/roster/shifts/${sid}`).then((r) => onShiftClick(r.data?.data || r.data)).catch(() => {}); }} onOpenGroup={onOpenGroup} onPublish={() => onPublish && onPublish(houseShifts)} />}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : house ? (
          <div className="space-y-4 mt-4">
            <Card>
              <CardContent className="p-3 space-y-2 text-sm">
                <div><p className="text-muted-foreground text-xs">Name</p><p className="font-medium">{house.name}</p></div>
                <div><p className="text-muted-foreground text-xs">House No.</p><p className="font-medium">{house.house_no || '—'}</p></div>
                <div><p className="text-muted-foreground text-xs">Address</p><p className="font-medium">{house.address || '—'}{house.suburb ? `, ${house.suburb}` : ''}{house.postcode ? ` ${house.postcode}` : ''}</p></div>
                <Separator />
                <div className="grid grid-cols-3 gap-2">
                  <div><p className="text-muted-foreground text-xs">Capacity</p><p className="font-medium">{house.capacity || 0}</p></div>
                  <div><p className="text-muted-foreground text-xs">Occupied</p><p className="font-medium">{allocations.length}</p></div>
                  <div><p className="text-muted-foreground text-xs">Available</p><p className="font-medium">{Math.max(0, (house.capacity || 0) - allocations.length)}</p></div>
                </div>
                {house.accessible && <Badge variant="outline" className="text-xs">Accessible</Badge>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Residents ({allocations.length})</CardTitle></CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5">
                {allocations.length > 0 ? allocations.map(a => (
                  <div key={a.id || a.participant_id} className="flex items-center justify-between bg-slate-50 rounded-md p-2">
                    <div>
                      <p className="text-xs font-medium">{a.participant_name || `Participant ${(a.participant_id || '').slice(-4)}`}</p>
                      {a.room_name && <p className="text-[10px] text-muted-foreground">Room: {a.room_name}</p>}
                    </div>
                    <Badge variant="outline" className="text-[10px]">{a.status}</Badge>
                  </div>
                )) : <p className="text-xs text-muted-foreground">No active residents</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-3 pt-3">
                <CardTitle className="text-sm flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Shifts in View ({houseShifts.length})</CardTitle>
                <p className="text-[10px] text-muted-foreground">{totalHours.toFixed(1)}h total</p>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5 max-h-72 overflow-y-auto">
                {houseShifts.length > 0 ? houseShifts.map(s => {
                  const style = getStatusStyle(s.status);
                  return (
                    <div key={s.id} onClick={() => { onShiftClick(s); }}
                      className={`rounded-md border px-2 py-1.5 cursor-pointer hover:shadow-sm ${style.bg}`}
                      data-testid={`sil-shift-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-[11px] font-semibold ${style.text}`}>{s.date} · {s.start_time}–{s.end_time}</span>
                        <Badge variant="outline" className="text-[9px]">{s.status}</Badge>
                      </div>
                      <p className="text-[10px] text-slate-600 truncate">{s.participant_name || '—'}</p>
                      <p className="text-[10px] text-slate-500 truncate">{s.assigned_staff?.map(a => a.name).join(', ') || 'Open shift'}</p>
                      {s.notes && <p className="text-[10px] italic text-slate-500 truncate" title={s.notes} data-testid={`sil-shift-notes-${s.id}`}>Notes: {s.notes}</p>}
                    </div>
                  );
                }) : <p className="text-xs text-muted-foreground">No shifts in this period</p>}
              </CardContent>
            </Card>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mt-6">House not found</p>
        )}
      </SheetContent>
    </Sheet>
  );
}
